import React, { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import DataTable from "../../../../../component/DataTable";
import { StatusChip } from "../../../../../component/listing";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { useNotify } from "../../../../../component/NotificationProvider";
import { cancelLeaveRequest } from "../../../../../services/leave";
import { formatLeaveRange } from "../../../../../utils/leaveMonth";
import { tokens, displayFont } from "../../../../../theme";
import { canWithdraw, cancelBlockReason, requestDays } from "./leaveFacts";

/**
 * The employee's own leave requests.
 *
 * Was a three-column table — from, to, status — behind a tab group, which is
 * the shape of a list you read but not one you act on. The book's version
 * carries the leave type, the number of days it cost, and, on a request that
 * can still be called off, a way to do it. That last one was the gap: the
 * screen had no cancel at all, so calling off leave you had booked meant
 * finding somebody with an admin login.
 *
 * A pending request can be withdrawn and approved leave cancelled before it
 * starts (`canWithdraw`). Until the server allowed it, the cancel button here
 * was answered with a 403 for every employee who pressed it.
 */

const TONE = {
  Approved: "success",
  Pending: "warning",
  Rejected: "danger",
  Cancelled: "muted",
};

const TABS = ["Approved", "Pending", "History"];

const LeaveData = ({ leaveData = [], typeById = {}, loading, onChanged }) => {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState("Approved");
  // the request being called off, held while its confirmation is open
  const [cancelling, setCancelling] = useState(null);
  const [working, setWorking] = useState(false);

  const rows = useMemo(() => {
    if (activeTab === "History") return leaveData;
    return leaveData.filter((r) => r?.status === activeTab);
  }, [leaveData, activeTab]);

  // Withdrawing a request nobody has decided gives nothing back, because
  // nothing was taken; cancelling approved leave returns the days. The copy
  // says which, so neither reads as the other.
  const pending = cancelling?.status === "Pending";

  const confirmCancel = async () => {
    if (!cancelling) return;
    setWorking(true);
    try {
      const res = await cancelLeaveRequest(cancelling._id);
      if (res?.success === false) throw new Error(res?.message || "Couldn't cancel this leave");
      notify.success(pending ? "Request withdrawn. Your manager has been told." : "Leave cancelled. The days are back in your balance.");
      setCancelling(null);
      onChanged?.();
    } catch (e) {
      notify.error(e?.message || "Couldn't cancel this leave");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "0.75rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.125rem", fontWeight: 600, color: tokens.text }}>
          Your requests
        </Box>
        <Stack direction="row" spacing={0.5}>
          {TABS.map((label) => (
            <Box
              key={label}
              component="button"
              type="button"
              onClick={() => setActiveTab(label)}
              aria-pressed={activeTab === label}
              sx={{
                font: "inherit",
                fontSize: "0.8125rem",
                fontWeight: 600,
                px: "0.75rem",
                py: "0.375rem",
                borderRadius: "8px",
                cursor: "pointer",
                border: `1px solid ${activeTab === label ? "transparent" : tokens.border}`,
                background: activeTab === label ? tokens.primary : "transparent",
                color: activeTab === label ? tokens.onPrimary : tokens.textMuted,
                "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
      </Stack>

      <DataTable
        rows={rows}
        loading={loading}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        empty={{
          title: activeTab === "History" ? "No leave requests yet" : `Nothing ${activeTab.toLowerCase()}`,
          description:
            activeTab === "History"
              ? "Apply for leave to see it here."
              : "Requests appear here as they are decided.",
        }}
        columns={[
          {
            key: "dates",
            label: "Dates",
            cellSx: { fontWeight: 600, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
            render: (r) =>
              formatLeaveRange(
                r.firstDay ? String(r.firstDay).slice(0, 10) : null,
                r.lastDay ? String(r.lastDay).slice(0, 10) : (r.firstDay ? String(r.firstDay).slice(0, 10) : null)
              ) || "—",
          },
          {
            key: "type",
            label: "Type",
            cellSx: { color: tokens.textMuted },
            render: (r) => typeById[String(r.leaveType)] || "—",
          },
          {
            key: "days",
            label: "Days",
            align: "right",
            width: "4.5rem",
            cellSx: { fontVariantNumeric: "tabular-nums" },
            render: (r) => requestDays(r) ?? "—",
          },
          {
            key: "status",
            label: "Status",
            width: "7rem",
            render: (r) => <StatusChip label={r.status} tone={TONE[r.status]} />,
          },
          {
            key: "action",
            label: "",
            width: "6rem",
            align: "right",
            render: (r) => {
              if (canWithdraw(r)) {
                return (
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCancelling(r); }}
                    sx={{
                      font: "inherit",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: tokens.danger,
                      background: "transparent",
                      border: 0,
                      p: 0,
                      cursor: "pointer",
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                    }}
                  >
                    {r.status === "Pending" ? "Withdraw" : "Cancel"}
                  </Box>
                );
              }
              /* A missing button with no explanation reads as something broken,
                 so a row that cannot be cancelled says why in the same place
                 the button would have been. */
              const why = cancelBlockReason(r);
              return why ? (
                <Box
                  title={why}
                  sx={{ fontSize: "0.75rem", color: tokens.textMuted, cursor: "help" }}
                >
                  Started
                </Box>
              ) : (
                <Box component="span" sx={{ color: tokens.textPlaceholder }}>—</Box>
              );
            },
          },
        ]}
      />

      {/* Cancelling is not undoable from this screen — rebooking means a new
          request and another approval — so it is confirmed, and the confirmation
          states what comes back rather than asking "are you sure". */}
      <Modal open={Boolean(cancelling)} onClose={() => setCancelling(null)} width="26rem">
        <ModalHead
          title={pending ? "Withdraw this request?" : "Cancel this leave?"}
          subtitle={
            cancelling
              ? formatLeaveRange(
                  String(cancelling.firstDay).slice(0, 10),
                  String(cancelling.lastDay || cancelling.firstDay).slice(0, 10)
                )
              : ""
          }
          onClose={() => setCancelling(null)}
        />
        <ModalBody>
          <Box sx={{ fontSize: "0.875rem", color: tokens.text, lineHeight: 1.6 }}>
            {pending
              ? "Nothing has been taken from your balance yet. Your manager will be told it is withdrawn."
              : cancelling && requestDays(cancelling)
                ? `${requestDays(cancelling)} day${requestDays(cancelling) === 1 ? "" : "s"} will go back into your balance.`
                : "The days will go back into your balance."}{" "}
            Asking for the same leave again means a new request and another approval.
          </Box>
        </ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={() => setCancelling(null)} sx={{ borderRadius: "8px" }}>
            Keep it
          </BtnSecondary>
          <BtnPrimary onClick={confirmCancel} disabled={working} sx={{ borderRadius: "8px" }}>
            {working ? (pending ? "Withdrawing…" : "Cancelling…") : pending ? "Withdraw request" : "Cancel leave"}
          </BtnPrimary>
        </ModalFoot>
      </Modal>
    </Box>
  );
};

export default LeaveData;
